import { useEffect, useState, useCallback, useMemo } from 'react';
// @mui
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
// styles
import './layout.css';
// icons
import StorefrontIcon from '@mui/icons-material/Storefront';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
// components
import Select from 'src/components/select';
import { LoadingScreen } from 'src/components/loading-screen';
// auth
import { useAuthContext } from 'src/auth/hooks';
// locales
import { useLocales } from 'src/locales';
// routes
import { paths } from 'src/routes/paths';
import { usePathname, useRouter } from 'src/routes/hooks';
// utils
import { firstLetterToUpperCase } from 'src/utils/change-case';
// types
import { CashRegister } from 'src/types/cashRegister';
import { IConfigItem, IPaymentReaderItem, IUserItem } from 'src/types/user';

interface SetupState {
  cashRegisterId: string;
  configId: string;
  paymentReaderId: string;
  userIds: string[];
}

type SetupStep = {
  id: number;
  label: string;
  description: string;
  icon: React.ReactNode;
  isValid: (state: SetupState, registers: CashRegister[]) => boolean;
};

const STEPS_CONFIG: SetupStep[] = [
  {
    id: 0,
    label: 'Caisse',
    description: 'Sélectionnez votre caisse',
    icon: <StorefrontIcon />,
    isValid: (state) => !!state.cashRegisterId,
  },
  {
    id: 1,
    label: 'Configuration',
    description: 'Choisissez la configuration',
    icon: <SettingsIcon />,
    isValid: (state) => !!state.configId,
  },
  {
    id: 2,
    label: 'Lecteur',
    description: 'Sélectionnez le lecteur de paiement (optionnel)',
    icon: <CreditCardIcon />,
    isValid: () => true,
  },
  {
    id: 3,
    label: 'Utilisateurs',
    description: 'Ajoutez les utilisateurs',
    icon: <GroupIcon />,
    isValid: () => true,
  },
  {
    id: 4,
    label: 'Confirmation',
    description: 'Vérifiez et confirmez',
    icon: <CheckCircleIcon />,
    isValid: () => true,
  },
];

// Step icon component
interface StepIconProps {
  step: number;
  active: boolean;
  completed: boolean;
}

const StepIcon = ({ step, active, completed }: StepIconProps) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: completed ? 'success.main' : active ? 'primary.main' : 'rgba(255,255,255,0.15)',
      color: completed || active ? 'white' : 'rgba(255,255,255,0.5)',
      transition: 'all 0.3s ease',
      '& svg': { fontSize: 18 },
    }}
  >
    {completed ? <CheckIcon fontSize="small" /> : STEPS_CONFIG[step]?.icon}
  </Box>
);

const StartLayout = ({ children }: { children: React.ReactNode }) => {
  const { user, cashRegisterLogin, onSelectConfig, updateCurrentUser } = useAuthContext();
  const { t } = useLocales();
  const router = useRouter();
  const pathname = usePathname();
  const salesPath = paths.dashboard.order.sales;
  const theme = useTheme();

  // State
  const [activeStepId, setActiveStepId] = useState(0);
  const [setupState, setSetupState] = useState<SetupState>({
    cashRegisterId: '',
    configId: '',
    paymentReaderId: '',
    userIds: [],
  });
  const [isCompletingSetup, setIsCompletingSetup] = useState(false);

  // Derived data with memoization
  const userDataReady = useMemo(
    () => user && Array.isArray(user.configs) && Array.isArray(user.cashRegisters),
    [user]
  );

  const cashRegisters = useMemo(() => user?.cashRegisters || [], [user?.cashRegisters]);
  const configs = useMemo<IConfigItem[]>(() => user?.configs || [], [user?.configs]);

  const selectedRegister = useMemo(
    () => cashRegisters.find((r: { id: string }) => r.id === setupState.cashRegisterId),
    [cashRegisters, setupState.cashRegisterId]
  );

  // Reset cashRegisterId if it's not in available registers
  useEffect(() => {
    if (setupState.cashRegisterId && cashRegisters.length > 0) {
      const isValid = cashRegisters.some((r) => r.id === setupState.cashRegisterId);
      if (!isValid) {
        setSetupState((prev) => ({ ...prev, cashRegisterId: '' }));
      }
    }
  }, [cashRegisters, setupState.cashRegisterId]);

  const selectedConfig = useMemo(
    () => configs.find((c: { id: string }) => c.id === setupState.configId),
    [configs, setupState.configId]
  );

  const availablePaymentReaders = useMemo(() => {
    if (!selectedConfig?.paymentMethods) return [];
    return selectedConfig.paymentMethods.reduce<IPaymentReaderItem[]>(
      (acc, method) => acc.concat(method.paymentReaders || []),
      []
    );
  }, [selectedConfig]);

  const availableUsers = useMemo(() => selectedConfig?.users || [], [selectedConfig]);

  // Pre-fill when selecting existing register
  useEffect(() => {
    if (!selectedRegister) return;

    const configMatch = configs.find((c) =>
      c.paymentMethods?.some(
        (pm) =>
          pm.paymentReaders?.some(
            (pr) =>
              pr?.id === selectedRegister.paymentReader?.id ||
              pr.idERP === selectedRegister.paymentReader?.idERP
          ) || pm.id === selectedRegister.paymentReader?.paymentMethod
      )
    );

    const userIds = Array.isArray(selectedRegister.users)
      ? selectedRegister.users
          .filter((u: IUserItem): u is IUserItem => !!u?.id)
          .map((u: IUserItem) => u.id)
      : [];

    setSetupState((prev) => ({
      ...prev,
      configId: configMatch?.id || prev.configId,
      paymentReaderId: selectedRegister.paymentReader?.id || prev.paymentReaderId,
      userIds: userIds.length ? userIds : prev.userIds,
    }));
  }, [selectedRegister, configs]);

  // Reset paymentReaderId if it's not in available options
  useEffect(() => {
    if (setupState.paymentReaderId && availablePaymentReaders.length > 0) {
      const isValid = availablePaymentReaders.some((r) => r.id === setupState.paymentReaderId);
      if (!isValid) {
        setSetupState((prev) => ({ ...prev, paymentReaderId: '' }));
      }
    }
  }, [availablePaymentReaders, setupState.paymentReaderId]);

  // Reset userIds if they're not in available users
  useEffect(() => {
    if (setupState.userIds.length > 0 && availableUsers.length > 0) {
      const validIds = setupState.userIds.filter((id) => availableUsers.some((u) => u.id === id));
      if (validIds.length !== setupState.userIds.length) {
        setSetupState((prev) => ({ ...prev, userIds: validIds }));
      }
    }
  }, [availableUsers, setupState.userIds]);

  const isStepValid = STEPS_CONFIG[activeStepId]?.isValid(setupState, cashRegisters) ?? false;
  const progress = ((activeStepId + 1) / STEPS_CONFIG.length) * 100;

  useEffect(() => {
    if (isCompletingSetup && pathname === salesPath) {
      setIsCompletingSetup(false);
    }
  }, [isCompletingSetup, pathname, salesPath]);

  const handleNext = useCallback(async () => {
    if (!isStepValid) return;

    // Select config step
    if (activeStepId === 1 && setupState.configId) {
      await onSelectConfig(setupState.configId);
    }

    // Final step: login
    if (activeStepId === 4 && setupState.cashRegisterId) {
      setIsCompletingSetup(true);

      try {
        await cashRegisterLogin(setupState.cashRegisterId);
        await updateCurrentUser();
        router.replace(salesPath);
      } catch (error) {
        setIsCompletingSetup(false);
        throw error;
      }
      return;
    }

    setActiveStepId((prev) => Math.min(prev + 1, STEPS_CONFIG.length - 1));
  }, [
    isStepValid,
    activeStepId,
    setupState.configId,
    setupState.cashRegisterId,
    onSelectConfig,
    cashRegisterLogin,
    updateCurrentUser,
    router,
    salesPath,
  ]);

  const handleBack = useCallback(() => {
    setActiveStepId((prev) => Math.max(prev - 1, 0));
  }, []);

  const updateState = useCallback(<K extends keyof SetupState>(key: K, value: SetupState[K]) => {
    setSetupState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Step content components
  const StepContent = useMemo(() => {
    const isExistingRegister = !!selectedRegister;

    switch (activeStepId) {
      case 0:
        return (
          <Fade in timeout={400}>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Sélectionnez une caisse
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Choisissez la caisse que vous souhaitez ouvrir
                </Typography>
              </Box>
              <Select
                value={setupState.cashRegisterId}
                setValue={(v) => updateState('cashRegisterId', v as string)}
                label={t(firstLetterToUpperCase('cash_register'))}
                items={cashRegisters.map((r: CashRegister) => ({ value: r.id, label: r.name }))}
              />
              {selectedRegister && (
                <Card variant="outlined" sx={{ bgcolor: 'background.neutral', borderColor: 'divider' }}>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', color: 'white' }}>
                        <StorefrontIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {selectedRegister.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {selectedRegister.id}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Fade>
        );

      case 1:
        return (
          <Fade in timeout={400}>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedConfig?.locationName}
                </Typography>
              </Box>
              <Select
                value={setupState.configId}
                setValue={(v) => updateState('configId', v as string)}
                label={t(firstLetterToUpperCase('config'))}
                items={configs.map((c) => ({ value: c.id, label: c.credential.accountNetsuite }))}
                disabled={isExistingRegister}
              />
              {selectedConfig && (
                <Card variant="outlined" sx={{ bgcolor: 'background.neutral', borderColor: 'divider' }}>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: 'success.main', color: 'white' }}>
                          <LocationOnIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {selectedConfig.locationName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Compte: {selectedConfig.credential.accountNetsuite}
                          </Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          size="small"
                          label={selectedConfig.currency?.code || 'EUR'}
                          variant="outlined"
                        />
                        {selectedConfig.changeItemPrice && (
                          <Chip
                            size="small"
                            label="Prix modifiable"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Fade>
        );

      case 2: {
        const selectedReader = availablePaymentReaders.find(
          (r) => r.id === setupState.paymentReaderId
        );
        return (
          <Fade in timeout={400}>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Lecteur de paiement
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sélectionnez le terminal de paiement à utiliser
                </Typography>
              </Box>
              <Select
                value={setupState.paymentReaderId}
                setValue={(v) => updateState('paymentReaderId', v as string)}
                label={t(firstLetterToUpperCase('payment_reader'))}
                items={availablePaymentReaders.map((r) => ({ value: r.id, label: r.name }))}
                disabled={isExistingRegister}
              />
              {selectedReader && (
                <Card variant="outlined" sx={{ bgcolor: 'background.neutral', borderColor: 'divider' }}>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ bgcolor: 'warning.main', color: 'white' }}>
                        <CreditCardIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {selectedReader.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Reader ID: {selectedReader.readerId}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              )}
            </Stack>
          </Fade>
        );
      }

      case 3:
        return (
          <Fade in timeout={400}>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Utilisateurs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sélectionnez les utilisateurs pour cette session
                </Typography>
              </Box>
              <Select
                value={setupState.userIds}
                setValue={(v) => updateState('userIds', v as string[])}
                label={t(firstLetterToUpperCase('users'))}
                items={availableUsers.map((u) => ({ value: u.id, label: u.displayName }))}
                multiple
                disabled={isExistingRegister}
              />
              {setupState.userIds.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {setupState.userIds.map((id) => {
                    const userItem = availableUsers.find((u) => u.id === id);
                    return userItem ? (
                      <Chip
                        key={id}
                        avatar={
                          <Avatar sx={{ bgcolor: 'primary.main', color: 'white' }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                        }
                        label={userItem.displayName}
                        variant="outlined"
                        color="primary"
                      />
                    ) : null;
                  })}
                </Stack>
              )}
            </Stack>
          </Fade>
        );

      case 4:
        return (
          <Fade in timeout={400}>
            <Stack spacing={3}>
              <Box textAlign="center">
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                  Confirmez la configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vérifiez les informations avant de continuer
                </Typography>
              </Box>
              <Card variant="outlined" sx={{ overflow: 'visible', borderColor: 'divider' }}>
                <CardContent sx={{ p: 0 }}>
                  <SummaryRow
                    icon={<StorefrontIcon color="primary" />}
                    label="Caisse"
                    value={selectedRegister?.name || '-'}
                  />
                  <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                  <SummaryRow
                    icon={<SettingsIcon color="success" />}
                    label="Configuration"
                    value={selectedConfig?.locationName || '-'}
                  />
                  <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                  <SummaryRow
                    icon={<CreditCardIcon color="warning" />}
                    label="Lecteur"
                    value={
                      availablePaymentReaders.find((r) => r.id === setupState.paymentReaderId)
                        ?.name || '-'
                    }
                  />
                  <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                  <SummaryRow
                    icon={<GroupIcon color="info" />}
                    label="Utilisateurs"
                    value={
                      setupState.userIds
                        .map(
                          (id) =>
                            availableUsers.find((u: { id: string }) => u.id === id)?.displayName
                        )
                        .filter(Boolean)
                        .join(', ') || '-'
                    }
                  />
                </CardContent>
              </Card>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'success.lighter',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'success.light',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleIcon color="success" />
                  <Typography variant="body2" color="success.dark" fontWeight={500}>
                    Tout est prêt ! Cliquez sur Terminer pour ouvrir la caisse.
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Fade>
        );

      default:
        return null;
    }
  }, [
    selectedRegister,
    activeStepId,
    setupState.cashRegisterId,
    setupState.configId,
    setupState.userIds,
    setupState.paymentReaderId,
    t,
    cashRegisters,
    configs,
    selectedConfig,
    availableUsers,
    availablePaymentReaders,
    updateState,
  ]);

  // Loading state
  if (isCompletingSetup) {
    return <LoadingScreen fullScreen message="Ouverture de la caisse..." />;
  }

  if (user && !userDataReady) {
    return <LoadingScreen fullScreen message="Chargement de votre espace de vente..." />;
  }

  if (!userDataReady) {
    return <>{children}</>;
  }

  // Authorized users bypass setup
  if (user?.role === 'Admin' || user?.cashRegister || user?.currentRegister) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#f5f6f8',
        p: { xs: 2, md: 4 },
      }}
      className="start-layout-bg"
    >
      {/* Subtle background shapes */}
      <Box className="floating-shape floating-shape-1" />
      <Box className="floating-shape floating-shape-2" />
      <Box className="floating-shape floating-shape-3" />

      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 880,
          minHeight: 580,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: '0 12px 40px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)',
          position: 'relative',
          zIndex: 10,
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        {/* Left Panel - Stepper (sober dark blue) */}
        <Box
          sx={{
            width: { xs: '100%', md: 300 },
            background: 'linear-gradient(180deg, #1e3a5f 0%, #152a45 100%)',
            color: 'white',
            p: { xs: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle decorative elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.04)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: -20,
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.03)',
            }}
          />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Configuration
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 4 }}>
            Ouvrir une caisse
          </Typography>

          <Stepper
            activeStep={activeStepId}
            orientation="vertical"
            sx={{
              '.MuiStepConnector-line': { borderColor: 'rgba(255,255,255,0.2)', minHeight: 20 },
              '.MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                borderColor: 'rgba(255,255,255,0.6)',
              },
              '.MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                borderColor: 'rgba(255,255,255,0.4)',
              },
            }}
          >
            {STEPS_CONFIG.map((step, index) => (
              <Step key={step.id} completed={index < activeStepId}>
                <StepLabel
                  StepIconComponent={() => (
                    <StepIcon
                      step={index}
                      active={index === activeStepId}
                      completed={index < activeStepId}
                    />
                  )}
                  sx={{
                    '.MuiStepLabel-label': {
                      color: 'rgba(255,255,255,0.5) !important',
                      fontWeight: index === activeStepId ? 600 : 400,
                      '&.Mui-active': { color: 'white !important' },
                      '&.Mui-completed': { color: 'rgba(255,255,255,0.8) !important' },
                    },
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={index === activeStepId ? 600 : 500}>
                      {step.label}
                    </Typography>
                    {index === activeStepId && (
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}
                      >
                        {step.description}
                      </Typography>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 'auto', pt: 4 }}>
            <Typography variant="caption" sx={{ opacity: 0.5 }}>
              Étape {activeStepId + 1} sur {STEPS_CONFIG.length}
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 3,
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2,
                mt: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${progress}%`,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  borderRadius: 2,
                  transition: 'width 0.4s ease',
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Right Panel - Content */}
        <Box sx={{ flex: 1, p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {StepContent}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 4, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStepId === 0}
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!isStepValid}
              endIcon={
                activeStepId === STEPS_CONFIG.length - 1 ? <CheckIcon /> : <ArrowForwardIcon />
              }
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                bgcolor: '#1e3a5f',
                '&:hover': { bgcolor: '#152a45' },
              }}
            >
              {activeStepId === STEPS_CONFIG.length - 1 ? 'Terminer' : 'Continuer'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

// Helper component for summary display
const SummaryRow = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2, gap: 2 }}>
    <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32 }}>{icon}</Avatar>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export default StartLayout;
